import { readContracts } from "@wagmi/core"
import BigNumber from "bignumber.js"
import { FunctionComponent, SVGProps } from "react"
import { create } from "zustand"
import { UNKNOWNIcon } from "../assets"
import { BorrowAndLendingDataType, DECIMALS, ORACLE_BASE_CURRENCY_UNIT_DECIMALS, PRICE_DECIMALS, RAY_DECIMALS, SECONDS_PER_YEAR, apiLinks, config, tokenMapping } from "../components"
import { Network, dataProviderContract, dataProviderContractAddresses } from "../contracts"
import { ERC20Abi } from "../contracts/ERC20"
import { interestRateStrategyABI } from "../contracts/interestRateStrategy"
import { oracleContract, oracleContractAddresses } from "../contracts/oracles"
import { poolContract, poolContractAddresses } from "../contracts/pool"
import { UIPoolDataProviderABI, UIPoolDataProviderAddress } from "../contracts/UIPoolDataProvider"

type ThemeType = 'dark' | 'light'

interface MarketContract {
    symbol: string, tokenAddress: string
}

export interface MarketData {
    icon: FunctionComponent<SVGProps<SVGSVGElement>>,
    asset: string,
    contractAddress: `0x${string}`,
    name: string,
    price: number,
    totalSupplied: number,
    totalSuppliedCap: number,
    supplyApy: number,
    totalBorrowed: number,
    totalBorrowedCap: number,
    borrowApy: number,
    utilizationRate: number,
    liquidityIndex?: number
    variableBorrowIndex?: number
    decimals?: number
    ltv?: number
    liquidationThreshold?: number
    liquidationBonus?: number
    reserveFactor?: number
    usageAsCollateralEnabled?: boolean
    borrowingEnabled?: boolean
    stableBorrowRateEnabled?: boolean
    isActive?: boolean
    isFrozen?: boolean
}

export interface UserDataPerMarket {
    icon: FunctionComponent<SVGProps<SVGSVGElement>>,
    asset: string,
    name: string,
    price: number,
    currentATokenBalance: number
    currentStableDebt: number
    currentVariableDebt: number
    principalStableDebt: number
    scaledVariableDebt: number
    stableBorrowRate: number
    liquidityRate: number
    stableRateLastUpdated: number
    usageAsCollateralEnabled: boolean
    currentScaledTokenBalance: number
}

export interface UserAccountData {
    totalCollateralBase: number
    totalDebtBase: number
    availableBorrowsBase: number
    currentLiquidationThreshold: number
    ltv: number
    healthFactor: number
}

type GlobalStore = {
    network: Network
    setNetwork: (network: Network) => void
    theme: ThemeType
    setTheme: (themeInput: ThemeType) => void
    userAddress: `0x${string}` | undefined
    userAccountData: UserAccountData | undefined
    setUserAddress: (address: `0x${string}` | undefined) => void
    userBorrowAndDepositData: BorrowAndLendingDataType[]
    userDataPerMarket: UserDataPerMarket[]
    userDataPerMarketLoading: boolean
    setUserDataPerMarket: (markets: MarketData[]) => void
    getUserDataPerMarket: () => Promise<void>
    markets: MarketData[]
    marketsLoading: boolean
    setMarkets: (markets: MarketData[]) => void
    getMarkets: () => Promise<void>
    getAllTokens: (forceReload?: boolean) => Promise<MarketContract[]>
    getAllTokensLoading: boolean
    allTokens: MarketContract[]
    seedStore: () => Promise<void>
    tokenDetailsPageHistoryCount: number
    setTokenDetailsPageHistoryCount: (count: number) => void
    getTokenAllowance: (tokenAddress: `0x${string}`, sender: `0x${string}`, spender: `0x${string}`) => Promise<any>
    utilizationRateChartLoading: boolean
    getUtilizationChart: (tokenAddress: `0x${string}`) => Promise<any>
    getMarketOverviewChart: (tokenAddress: `0x${string}`, from: number, until: number) => Promise<any>
    get30DayAvgApy: (tokenAddress: `0x${string}`, from: number, until: number) => Promise<any>
}

const useGlobalStore = create<GlobalStore>((set, get) => ({
    network: `${process.env.REACT_APP_NETWORK ?? 'testnet'}` as Network,
    setNetwork: (network: Network) => set(() => ({ network })),
    theme: 'dark',
    setTheme: (themeInput: ThemeType) => set(() => ({ theme: themeInput })),
    setUserAddress: (address: `0x${string}` | undefined) => set(() => ({ userAddress: address })),
    //all tokens
    allTokens: [],
    getAllTokensLoading: false,
    getAllTokens: async (forceReload) => {
        //fetch markets data
        if (get().allTokens.length && get().getAllTokensLoading === false && !forceReload) return get().allTokens
        set(() => ({ getAllTokensLoading: true }))
        const result = await readContracts(config, {
            contracts: [
                {
                    ...dataProviderContract,
                    functionName: 'getAllReservesTokens',
                },
            ],
        })
        const markets = result[0]?.result
        set(() => ({ allTokens: [...markets ?? []], getAllTokensLoading: false }))
        return [...markets ?? []]
    },

    //user data
    userAccountData: undefined,
    userBorrowAndDepositData: [],
    userAddress: undefined,
    userDataPerMarket: [],
    userDataPerMarketLoading: false,
    setUserDataPerMarket: (userDataPerMarket: any[]) => set(() => ({ userDataPerMarket })),
    getUserDataPerMarket: async () => {
        set(() => ({ userDataPerMarketLoading: true })) //set loading to true
        if (get().userAddress === undefined) return

        //prep contract addresses
        const dataProviderContractAddress = dataProviderContractAddresses[get().network]
        const oracleContractAddress = oracleContractAddresses[get().network]
        const poolContractAddress = poolContractAddresses[get().network]
        const poolProviderAddress = `0x108fFADF2cA68c1b274CD5193454C33e9eBba7A4` as `0x${string}`
        //fetch markets data
        const rawMarkets = await get().getAllTokens()
        const userInfoQuery = rawMarkets.map((market: MarketContract) => {
            return {
                ...dataProviderContract,
                address: dataProviderContractAddress,
                functionName: 'getUserReserveData',
                args: [market.tokenAddress, get().userAddress],
            }
        })

        const marketsPriceQuery = rawMarkets.map((market: MarketContract) => {
            return {
                ...oracleContract,
                address: oracleContractAddress,
                functionName: 'getAssetPrice',
                args: [market.tokenAddress],
            }
        })

        const uiProviderDataQuery = {
            abi: UIPoolDataProviderABI as any,
            address: UIPoolDataProviderAddress[get().network],
            functionName: 'getUserReservesData',
            args: [poolProviderAddress, get().userAddress!],
        }

        const uiProviderMarketDataQuery = {
            abi: UIPoolDataProviderABI as any,
            address: UIPoolDataProviderAddress[get().network],
            functionName: 'getReservesData',
            args: [poolProviderAddress],
        }

        const usersInfoPerMarketRaw = await readContracts(config, {
            contracts: [...userInfoQuery, ...marketsPriceQuery, uiProviderDataQuery, uiProviderMarketDataQuery],
        })

        const userAccountDataQuery = await readContracts(config, {
            contracts: [{
                ...poolContract,
                address: poolContractAddress,
                functionName: 'getUserAccountData',
                args: [get().userAddress!]
            }]
        })

        const userAccountData = userAccountDataQuery[0].result!
        const totalCollateralBase = new BigNumber(userAccountData[0]?.toString()).shift(-PRICE_DECIMALS).toNumber()
        const totalDebtBase = new BigNumber(userAccountData[1]?.toString()).shift(-PRICE_DECIMALS).toNumber()
        const availableBorrowsBase = new BigNumber(userAccountData[2]?.toString()).shift(-PRICE_DECIMALS).toNumber()
        const currentLiquidationThreshold = new BigNumber(userAccountData[3]?.toString()).shift(-2).toNumber()
        const ltv = new BigNumber(userAccountData[4]?.toString()).shift(-2).toNumber()
        const healthFactor = new BigNumber(userAccountData[5]?.toString()).shift(-DECIMALS).toNumber()
        set(() => ({
            userAccountData: {
                totalCollateralBase,
                totalDebtBase,
                availableBorrowsBase,
                currentLiquidationThreshold,
                ltv,
                healthFactor
            }
        }))

        const uiData = (usersInfoPerMarketRaw as any)[usersInfoPerMarketRaw.length - 2].result[0]
        // const uiMarketData = (usersInfoPerMarketRaw as any)[usersInfoPerMarketRaw.length - 1].result[0]

        // // get user total debt with interest
        // const debtTokenAddressesQuery = uiMarketData.map((market: any, index: number) => {
        //     return {
        //         abi: variableDebtTokenABI,
        //         address: market.variableDebtTokenAddress,
        //         functionName: 'balanceOf',
        //         args: [get().userAddress],
        //     }
        // })
        // const userDebtPerToken = await readContracts(config, {
        //     contracts: [debtTokenAddressesQuery],
        // })

        const userInfoPerMarket = (usersInfoPerMarketRaw ?? []).map((userInfoRaw, index: number) => {
            const userInfo: (string | number)[] = userInfoRaw?.result as any
            if (!userInfo) return null
            if (index >= rawMarkets.length ?? 0) return null
            const priceRaw = usersInfoPerMarketRaw[index + (rawMarkets.length)]?.result as number | string
            const marketInfo = get().markets.find((market) => market.asset === rawMarkets[index].symbol)
            if (!marketInfo) return null
            const { liquidityIndex, variableBorrowIndex } = marketInfo
            if (!liquidityIndex || !variableBorrowIndex) return null
            const marketUIData = uiData.find((o: any) => o.underlyingAsset?.toLowerCase() === marketInfo.contractAddress?.toLowerCase())
            if (!marketUIData) return null
            const currentATokenBalance = new BigNumber(marketUIData.scaledATokenBalance ?? 0)
            const currentScaledTokenBalance = currentATokenBalance.mul(liquidityIndex.toString()).shift(-(marketInfo?.decimals ?? DECIMALS)).toNumber()

            return {
                icon: tokenMapping[rawMarkets[index].symbol.toUpperCase()]?.iconUrl ?? UNKNOWNIcon,
                asset: rawMarkets[index].symbol,
                name: tokenMapping[rawMarkets[index].symbol.toUpperCase()]?.name ?? 'Unknown',
                price: new BigNumber(priceRaw ?? 0).shift(-ORACLE_BASE_CURRENCY_UNIT_DECIMALS).toNumber(),

                currentScaledTokenBalance,
                currentATokenBalance: new BigNumber(userInfo[0]).shift(-(marketInfo?.decimals ?? DECIMALS)).toNumber(),
                currentStableDebt: new BigNumber(userInfo[1]).shift(-(marketInfo?.decimals ?? DECIMALS)).toNumber(),
                currentVariableDebt: new BigNumber(userInfo[2]).shift(-(marketInfo?.decimals ?? DECIMALS)).toNumber(),
                principalStableDebt: new BigNumber(userInfo[3]).shift(-(marketInfo?.decimals ?? DECIMALS)).toNumber(),
                scaledVariableDebt: new BigNumber(userInfo[4]).mul(variableBorrowIndex.toString()).shift(-(marketInfo?.decimals ?? DECIMALS)).toNumber(),
                stableBorrowRate: new BigNumber(userInfo[5]).shift(-RAY_DECIMALS).toNumber(),
                liquidityRate: new BigNumber(userInfo[6]).shift(-RAY_DECIMALS).toNumber(),
                stableRateLastUpdated: new BigNumber(userInfo[7]).shift(-(marketInfo?.decimals ?? DECIMALS)).toNumber(),
                usageAsCollateralEnabled: !!userInfo[8],
            }
        }).filter((o) => o !== null) //filter away failed queries

        const userBorrowAndDepositData = usersInfoPerMarketRaw.map((userInfoRaw, index) => {
            const userInfo: (string | number)[] = userInfoRaw?.result as any
            if (!userInfo) return null
            if (index >= rawMarkets.length ?? 0) return null
            const marketInfo = get().markets.find((market) => market.asset === rawMarkets[index].symbol)
            if (!marketInfo) return null
            const { supplyApy, borrowApy, liquidityIndex, variableBorrowIndex } = marketInfo
            if (!liquidityIndex || !variableBorrowIndex) return null
            const marketUIData = uiData.find((o: any) => o.underlyingAsset?.toLowerCase() === marketInfo.contractAddress?.toLowerCase())
            if (!marketUIData) return null
            const currentATokenBalance = new BigNumber(marketUIData.scaledATokenBalance ?? 0)
            const currentScaledTokenBalance = currentATokenBalance.mul(liquidityIndex.toString()).shift(-(marketInfo?.decimals ?? DECIMALS)).toNumber()

            const priceRaw = usersInfoPerMarketRaw[index + (rawMarkets.length)]?.result as number | string
            const price = new BigNumber(priceRaw ?? 0).shift(-ORACLE_BASE_CURRENCY_UNIT_DECIMALS).toNumber()
            // const currentVariableDebt = new BigNumber(userInfo[2]).shift(-(marketInfo?.decimals ?? DECIMALS)).toNumber()
            // const principalStableDebt = new BigNumber(userInfo[3]).shift(-(marketInfo?.decimals ?? DECIMALS)).toNumber()

            const scaledVariableDebt = new BigNumber(userInfo[4]).mul(variableBorrowIndex.toString()).shift(-(marketInfo?.decimals ?? DECIMALS)).toNumber()
            const liquidityRate = new BigNumber(userInfo[6]).shift(-RAY_DECIMALS).toNumber() //TODO: make sure this is the net APY
            return {
                // currentATokenBalance   uint256 :  0
                // currentStableDebt   uint256 :  0
                // currentVariableDebt   uint256 :  0
                // principalStableDebt   uint256 :  0
                // scaledVariableDebt   uint256 :  0
                // stableBorrowRate   uint256 :  0
                // liquidityRate   uint256 :  914344737932165071158048
                // stableRateLastUpdated   uint40 :  0
                // usageAsCollateralEnabled   bool :  false
                icon: tokenMapping[rawMarkets[index].symbol.toUpperCase()]?.iconUrl ?? UNKNOWNIcon,
                asset: rawMarkets[index].symbol,
                name: tokenMapping[rawMarkets[index].symbol.toUpperCase()]?.name ?? 'Unknown',
                price: price,

                netApy: liquidityRate,
                supplyApy: supplyApy ?? 0,
                supply: currentScaledTokenBalance,
                supplyValue: currentScaledTokenBalance * price,
                borrowApy: borrowApy ?? 0,
                borrow: scaledVariableDebt,
                borrowValue: (scaledVariableDebt) * price
            }
        }).filter((o) => o !== null)

        set(() => ({
            userDataPerMarket: userInfoPerMarket as UserDataPerMarket[],
            userBorrowAndDepositData: userBorrowAndDepositData as BorrowAndLendingDataType[],
            userDataPerMarketLoading: false
        }))
    },

    // markets
    markets: [],
    marketsLoading: false,
    setMarkets: (markets: any[]) => set(() => ({ markets })),
    getMarkets: async () => {
        console.log("prod getMarket started")
        set(() => ({ marketsLoading: true })) //set loading to true

        //prep contract addresses
        const dataProviderContractAddress = dataProviderContractAddresses[get().network]
        const oracleContractAddress = oracleContractAddresses[get().network]

        //fetch markets data
        const rawMarkets = await get().getAllTokens()
        const marketsInfoQuery = rawMarkets.map((market: MarketContract) => {
            return {
                ...dataProviderContract,
                address: dataProviderContractAddress,
                functionName: 'getReserveData',
                args: [market.tokenAddress],
            }
        })

        const marketsCapQuery = rawMarkets.map((market: MarketContract) => {
            return {
                ...dataProviderContract,
                address: dataProviderContractAddress,
                functionName: 'getReserveCaps',
                args: [market.tokenAddress],
            }
        })

        const marketsLiquidationConfigQuery = rawMarkets.map((market: MarketContract) => {
            return {
                ...dataProviderContract,
                address: dataProviderContractAddress,
                functionName: 'getReserveConfigurationData',
                args: [market.tokenAddress],
            }
        })

        const marketsPriceQuery = rawMarkets.map((market: MarketContract) => {
            return {
                ...oracleContract,
                address: oracleContractAddress,
                functionName: 'getAssetPrice',
                args: [market.tokenAddress],
            }
        })

        const marketsInfoRaw = await readContracts(config, {
            contracts: [
                ...marketsInfoQuery,
                ...marketsCapQuery,
                ...marketsLiquidationConfigQuery,
                ...marketsPriceQuery
            ],
        })

        const marketsInfo = (marketsInfoRaw ?? []).map((marketInfoRaw, index: number) => {
            const marketInfo: (string | number)[] = marketInfoRaw?.result as any
            if (!marketInfo) return null
            if (index >= rawMarkets.length ?? 0) return null
            const [supplyCap, borrowCap] = marketsInfoRaw[index + (rawMarkets.length)]?.result as any
            const liquidationConfigRaw: number[] | string[] = marketsInfoRaw[index + (rawMarkets.length * 2)]?.result as any
            const priceRaw = marketsInfoRaw[index + (rawMarkets.length * 3)]?.result as number | string
            const liquidityIndex = new BigNumber(marketInfo[9]).shift(-RAY_DECIMALS).toNumber()
            const hardcodedTokenInfo = tokenMapping[rawMarkets[index].symbol.toUpperCase()]
            const marketDecimal = new BigNumber(liquidationConfigRaw[0]).toNumber()

            const liquidityRate = new BigNumber(marketInfo[5]).shift(-RAY_DECIMALS)
            const borrowRate = new BigNumber(marketInfo[6]).shift(-RAY_DECIMALS)

            const supplyApy = ((((liquidityRate.div(SECONDS_PER_YEAR)).plus(1)).toNumber() ** SECONDS_PER_YEAR) - 1) * 100
            const borrowApy = ((((1 + (borrowRate.div(SECONDS_PER_YEAR).toNumber())) ** SECONDS_PER_YEAR)) - 1) * 100

            return {
                // unbacked: new BigNumber(marketInfo[0]).toString(),
                // accruedToTreasuryScaled: new BigNumber(marketInfo[1]).toString(),
                // totalAToken: new BigNumber(marketInfo[2]).toString(),
                // totalStableDebt: new BigNumber(marketInfo[3]).toString(),
                // totalVariableDebt: new BigNumber(marketInfo[4]).toString(),
                // liquidityRate: new BigNumber(marketInfo[5]).toString(),
                // variableBorrowRate: new BigNumber(marketInfo[6]).toString(),
                // stableBorrowRate: new BigNumber(marketInfo[7]).toString(),
                // averageStableBorrowRate: new BigNumber(marketInfo[8]).toString(),
                // variableBorrowIndex: new BigNumber(marketInfo[10]).toString(),
                // lastUpdateTimestamp: new BigNumber(marketInfo[11]).toString(),
                liquidityIndex: liquidityIndex,
                variableBorrowIndex: new BigNumber(marketInfo[10]).shift(-RAY_DECIMALS).toNumber(),
                icon: hardcodedTokenInfo?.iconUrl ?? UNKNOWNIcon,
                asset: rawMarkets[index].symbol,
                contractAddress: rawMarkets[index].tokenAddress,
                name: hardcodedTokenInfo?.name ?? 'Unknown',
                price: new BigNumber(priceRaw ?? 0).shift(-ORACLE_BASE_CURRENCY_UNIT_DECIMALS).toNumber(), //TODO: Change to dynamic base unit decimal
                totalSupplied: new BigNumber(marketInfo[2]).shift(-(marketDecimal ?? DECIMALS)).mul(liquidityIndex.toString()).toNumber(), //mul by liquidityIndex to include interest
                totalSuppliedCap: new BigNumber(supplyCap ?? 0).shift(0).toNumber(), //TODO: double check but seems like dont need to shift
                supplyApy,
                totalBorrowed: new BigNumber(marketInfo[4]).shift(-(marketDecimal ?? DECIMALS)).toNumber(),
                totalBorrowedCap: new BigNumber(borrowCap ?? 0).shift(0).toNumber(), //TODO: double check but seems like dont need to shift
                borrowApy,
                utilizationRate: new BigNumber(marketInfo[4]).eq(0) ? 0 : new BigNumber(marketInfo[4]).div(new BigNumber(marketInfo[2])).mul(100).toNumber(),

                decimals: new BigNumber(liquidationConfigRaw[0]).toNumber(),
                ltv: new BigNumber(liquidationConfigRaw[1]).shift(-2).toNumber(), //-2 to %
                liquidationThreshold: new BigNumber(liquidationConfigRaw[2]).shift(-2).toNumber(), //-2 to %
                liquidationBonus: new BigNumber(liquidationConfigRaw[3]).shift(-2).minus(100).toNumber(), //-2 to %
                reserveFactor: new BigNumber(liquidationConfigRaw[4]).shift(-2).toNumber(), //-2 to %
                usageAsCollateralEnabled: !!liquidationConfigRaw[5],
                borrowingEnabled: !!liquidationConfigRaw[6],
                stableBorrowRateEnabled: !!liquidationConfigRaw[7],
                isActive: !!liquidationConfigRaw[8],
                isFrozen: !!liquidationConfigRaw[9],
            }
        }).filter((o) => o !== null) //filter away failed queries
        console.log("prod getMarket:", marketsInfo)
        set(() => ({ markets: marketsInfo as MarketData[], marketsLoading: false }))
    },

    getTokenAllowance: async (tokenAddress: `0x${string}`, sender: `0x${string}`, spender: `0x${string}`) => {
        const allowanceQuery = await readContracts(config, {
            contracts: [
                {
                    address: tokenAddress,
                    abi: ERC20Abi,
                    functionName: 'allowance',
                    args: [sender, spender],
                },
            ],
        })
        return new BigNumber(allowanceQuery[0]?.result?.toString() ?? 0).shift(-DECIMALS).toNumber()
    },
    tokenDetailsPageHistoryCount: -1,
    setTokenDetailsPageHistoryCount: (count: number) => set(() => ({ tokenDetailsPageHistoryCount: count })),
    utilizationRateChartLoading: false,
    getUtilizationChart: async (tokenAddress: `0x${string}`) => {
        set(() => ({ utilizationRateChartLoading: true }))
        const interestRateStratAddressQuery = await readContracts(config, {
            contracts: [
                {
                    ...dataProviderContract,
                    functionName: 'getInterestRateStrategyAddress',
                    args: [tokenAddress],
                },
            ],
        })
        const interestRateStratAddress = interestRateStratAddressQuery[0]?.result
        if (!interestRateStratAddress) return undefined

        const interestRateStratQuery = await readContracts(config, {
            contracts: [
                {
                    abi: interestRateStrategyABI,
                    address: interestRateStratAddress,
                    functionName: 'OPTIMAL_USAGE_RATIO',
                },
                {
                    abi: interestRateStrategyABI,
                    address: interestRateStratAddress,
                    functionName: 'getBaseVariableBorrowRate',
                },
                {
                    abi: interestRateStrategyABI,
                    address: interestRateStratAddress,
                    functionName: 'getVariableRateSlope1',
                },
                {
                    abi: interestRateStrategyABI,
                    address: interestRateStratAddress,
                    functionName: 'getVariableRateSlope2',
                },
            ],
        })
        const optimalUsageRatio = new BigNumber(interestRateStratQuery[0]?.result?.toString() ?? 0).shift(-(RAY_DECIMALS - 2))
        const baseVariableBorrowRate = new BigNumber(interestRateStratQuery[1]?.result?.toString() ?? 0).shift(-(RAY_DECIMALS - 2))
        const variableRateSlope1 = new BigNumber(interestRateStratQuery[2]?.result?.toString() ?? 0).shift(-(RAY_DECIMALS))
        const variableRateSlope2 = new BigNumber(interestRateStratQuery[3]?.result?.toString() ?? 0).shift(-(RAY_DECIMALS))
        //generate utilization rate
        let utilizationRates = []
        for (let i = 0; i <= 100; i += 0.5) {
            utilizationRates.push(new BigNumber(i))
        }

        const markets = get().markets
        const market = markets.find((market) => market.contractAddress === tokenAddress)
        const chartData = utilizationRates.map((utilizationRate) => {
            let borrowApr
            if (utilizationRate.lte(optimalUsageRatio)) {
                borrowApr = baseVariableBorrowRate.plus((utilizationRate.dividedBy(optimalUsageRatio)).times(variableRateSlope1))

            } else {
                borrowApr = baseVariableBorrowRate.plus(variableRateSlope1)
                    .plus(
                        ((utilizationRate.minus(optimalUsageRatio)).dividedBy(new BigNumber(100).minus(optimalUsageRatio)))
                            .times(variableRateSlope2)
                    )
            }

            // St = U(Vt)(1 - Rt) /100 to convert borrowApr
            //TODO: to confirm if this is correct
            const supplyApy = utilizationRate.mul(borrowApr).mul(1 - Number((market?.reserveFactor ?? 0) / 100)).div(100)

            return {
                utilizationRate: utilizationRate.toNumber(), xAxisName: utilizationRate.toNumber() + "%",
                borrowApy: borrowApr.mul(100).toNumber(), yAxisName: borrowApr.mul(100).toNumber().toFixed(2) + "%",
                supplyApy: supplyApy.mul(100).toNumber(), yAxisSupplyName: supplyApy.mul(100).toNumber().toFixed(2) + "%",
            }
        })
        set(() => ({ utilizationRateChartLoading: false }))
        return { chartData, optimalUsageRatio: optimalUsageRatio.toNumber() }
    },

    getMarketOverviewChart: async (assetAddress, from, until) => {
        const market = get().markets.find((market) => market.contractAddress === assetAddress)
        const response = await fetch(`${apiLinks.testnet}/market/info?asset=${assetAddress}&from=${from}&until=${until}`)
        const data = await response.json();
        return data?.result?.entries?.map((o: any) => {
            const parsedData = {
                date: o.date,
                net_supplied: new BigNumber(o?.net_supplied ?? 0).shift(-(market?.decimals ?? 0)).toNumber(),
                net_borrowed: new BigNumber(o?.net_borrowed ?? 0).shift(-(market?.decimals ?? 0)).toNumber(),
                borrow_apy: new BigNumber(o.borrow_APY).shift(2).toNumber(),
                supply_apy: new BigNumber(o.supply_APY).shift(2).toNumber(),
            }
            return parsedData
        })
    },
    get30DayAvgApy: async (assetAddress, from, until) => {
        const response = await fetch(`${apiLinks.testnet}/market/info?asset=${assetAddress}&from=${from}&until=${until}`)
        const data = await response.json();
        const avg30DaysBorrowApy = data?.result?.entries?.reduce((acc: number, o: any) => acc + new BigNumber(o.borrow_APY).shift(2).toNumber(), 0) / data?.result?.entries?.length ?? 0
        const avg30DaysSupplyApy = data?.result?.entries?.reduce((acc: number, o: any) => acc + new BigNumber(o.supply_APY).shift(2).toNumber(), 0) / data?.result?.entries?.length ?? 0
        return { avg30DaysBorrowApy, avg30DaysSupplyApy }
    },
    seedStore: async () => {
        // the sequence matters, eg. getUserDataPerMarket relies on getMarkets
        await get().getAllTokens(true)
        await get().getMarkets()
        await get().getUserDataPerMarket()
        // await get().getUtilizationChart("0x97189Fa5B08A6F8Ceb2aa4CD4a45429dC0978eb7")
    }
}))

export default useGlobalStore